import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    Grid,
    Alert,
    Divider,
    InputAdornment,
    IconButton,
    Typography,
    Card,
    CardContent,
    Button,
    Stack,
    Avatar,
    Chip
} from '@mui/material';
import {
    Person as PersonIcon,
    Visibility,
    VisibilityOff,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ArrowBack as ArrowBackIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    CalendarMonth as CalendarIcon,
    Badge as BadgeIcon,
    CardMembership as CardMembershipIcon
} from '@mui/icons-material';
import GenericPage, { useDefaultViewMode } from '../../Commun/components/GenericPage';
import GenericTable from '../../Commun/components/GenericTable';
import { ZoomField } from '../../Commun/components/ZoomField';
import { ZoomData } from '../../Commun/services/blobticZoomService';
import { blobticService } from '../../Commun/services/blobticService';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { BlobticDataManager } from '../../Commun/services/blobticDataManager';

export interface UserFormData {
    // Primary Key
    USR_IDF?: string;
    USR_UIDF?: string;

    // User Zoom (Primary)
    USR_CODE: string;
    USR_LIB: string;
    USR_PASS?: string;

    // Profil Zoom (Secondary)
    PRF_IDF?: string;
    PRF_CODE?: string;
    PRF_LIB?: string;

    // License Zoom (Secondary)
    USERDATA_IDF?: string;
    USERDATA_CODE?: string;
    USERDATA_LIB?: string;

    // Hidden / Links
    USER_IDF?: string;
    DOS_IDF_DEFAUT?: string;
    JOB_IDF?: string;
    USR_DATA?: string;

    // Dates & Info
    USR_START_DATE?: string;
    USR_END_DATE?: string;
    USR_PASS_DATE?: string;
    USR_PASS_DAYS?: string | number;
    USR_TELPOR?: string;
    USR_MAIL?: string;
    USR_MMO?: string;

    // Audit
    LAST_UPDT?: string;
    USER_LIB?: string;
}

const initialForm: UserFormData = {
    USR_CODE: '',
    USR_LIB: '',
    USR_PASS: ''
};

const UsersPage: React.FC = () => {
    const { t } = useLanguage();

    // View state: 'list' (default) or 'form' (new/edit)
    const [view, setView] = useState<'list' | 'form'>('list');

    // List view mode: 'grid' or 'table' (initialized from user default preference)
    const [viewMode, setViewMode] = useDefaultViewMode();

    // Users list
    const [usersList, setUsersList] = useState<UserFormData[]>([]);
    const [listLoading, setListLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState<UserFormData>(initialForm);
    const [error, setError] = useState<string | null>(null);
    const [showPassword, setShowPassword] = useState(false);

    // Load users from IndexedDB cache / Server DB
    const loadUsers = async (forceRefresh: boolean = false) => {
        setListLoading(true);
        try {
            const response = await blobticService.executeZoomRequest<UserFormData[]>({
                ZoomKey: 'user_list',
                Action: {
                    OKey: 'Blobtic_Sys_User,sys_user,select'
                }
            }, !forceRefresh);

            if (response.success && Array.isArray(response.data)) {
                setUsersList(response.data);
            }
        } catch (err: any) {
            console.error('Error loading users', err);
        } finally {
            setListLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
        // Reactive sync with IndexedDB / WebSocket mutations
        const unsubscribe = BlobticDataManager.subscribe((obj) => {
            if (obj === 'sys_user') {
                loadUsers();
            }
        });
        return () => unsubscribe();
    }, []);

    // ── Navigation & Actions ───────────────────────────────────────────────────

    const openNew = () => {
        setFormData(initialForm);
        setError(null);
        setShowPassword(false);
        setView('form');
    };

    const openEdit = (user: UserFormData) => {
        setFormData({ ...user });
        setError(null);
        setShowPassword(false);
        setView('form');
    };

    const backToList = () => {
        setView('list');
        setFormData(initialForm);
        setError(null);
        setShowPassword(false);
        loadUsers();
    };

    const handleDelete = async (user: UserFormData) => {
        if (!user.USR_IDF) return;

        const confirmed = await blobticMessage.confirm({
            title: t('common.confirm.title.delete'),
            message: t('page.users.confirm_delete', user.USR_LIB || user.USR_CODE),
            severity: 'warning',
            confirmText: t('common.actions.confirm'),
            cancelText: t('btn.cancel')
        });

        if (!confirmed) return;

        try {
            const res = await blobticService.executeZoomRequest({
                ZoomKey: 'user_delete',
                Action: {
                    OKey: 'Blobtic_Sys_User,sys_user,delete',
                    USR_IDF: user.USR_IDF
                }
            }, false);

            if (res.success) {
                blobticMessage.success(t('common.messages.success'), t('common.messages.deleted_success'));
                loadUsers();
            } else {
                blobticMessage.error(t('common.error'), res.message || 'Erreur lors de la suppression.');
            }
        } catch (e: any) {
            blobticMessage.error(t('common.error'), e.message);
        }
    };

    // ── Form Handlers ──────────────────────────────────────────────────────────

    const handleInputChange = (field: keyof UserFormData, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleZoomSelect = (selectedData: ZoomData, zoomKey: string) => {
        if (zoomKey === 'profil') {
            setFormData(prev => ({
                ...prev,
                PRF_IDF: selectedData.PRF_IDF,
                PRF_CODE: selectedData.PRF_CODE,
                PRF_LIB: selectedData.PRF_LIB
            }));
        } else if (zoomKey === 'license') {
            setFormData(prev => ({
                ...prev,
                USERDATA_IDF: selectedData.USERDATA_IDF,
                USERDATA_CODE: selectedData.USERDATA_CODE,
                USERDATA_LIB: selectedData.USERDATA_LIB
            }));
        }
    };

    const handleZoomChange = (value: string, selectedData: ZoomData | undefined, zoomKey: string) => {
        if (selectedData) {
            handleZoomSelect(selectedData, zoomKey);
        } else if (!value) {
            if (zoomKey === 'profil') {
                setFormData(prev => ({ ...prev, PRF_IDF: undefined, PRF_CODE: '', PRF_LIB: '' }));
            } else if (zoomKey === 'license') {
                setFormData(prev => ({ ...prev, USERDATA_IDF: undefined, USERDATA_CODE: '', USERDATA_LIB: '' }));
            }
        }
    };

    const handleClear = () => {
        setFormData(initialForm);
        setError(null);
        setShowPassword(false);
    };

    const validate = () => {
        if (!formData.USR_CODE || !formData.USR_LIB) {
            return t('page.users.validation.required') || 'Code et Nom sont obligatoires';
        }
        return null;
    };

    // ─── LIST VIEW ─────────────────────────────────────────────────────────────

    if (view === 'list') {
        return (
            <GenericPage
                title={t('page.users.title') || "Gestion des Utilisateurs"}
                icon={<PersonIcon />}
                footerEnabled={false}
                viewMode={viewMode}
                onViewModeChange={setViewMode}
                onNew={openNew}
                newButtonLabel={t('page.users.add_new') || "Nouvel Utilisateur"}
            >
                {listLoading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography color="text.secondary">Chargement en cours...</Typography>
                    </Box>
                ) : viewMode === 'grid' ? (
                    /* ── GRID VIEW ── */
                    <Grid container spacing={2.5}>
                        {usersList.map(user => {
                            const userInitials = (user.USR_LIB || user.USR_CODE || 'U')
                                .split(' ')
                                .map(n => n[0])
                                .slice(0, 2)
                                .join('')
                                .toUpperCase();

                            return (
                                <Grid item xs={12} sm={6} md={4} lg={3} key={user.USR_IDF || user.USR_CODE}>
                                    <Card
                                        variant="outlined"
                                        sx={{
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            borderRadius: 3,
                                            borderColor: 'divider',
                                            bgcolor: 'background.paper',
                                            transition: 'all 0.2s',
                                            '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' }
                                        }}
                                    >
                                        <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                                            {/* Header with Avatar, Name, Code */}
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                                                <Avatar
                                                    variant="rounded"
                                                    sx={{
                                                        width: 48,
                                                        height: 48,
                                                        borderRadius: 2,
                                                        bgcolor: 'primary.main',
                                                        color: 'primary.contrastText',
                                                        fontWeight: 'bold'
                                                    }}
                                                >
                                                    {userInitials}
                                                </Avatar>
                                                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                                    <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                                        {user.USR_LIB}
                                                    </Typography>
                                                    <Chip
                                                        label={user.USR_CODE}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600 }}
                                                    />
                                                </Box>
                                            </Box>

                                            <Divider sx={{ my: 1.5 }} />

                                            {/* Profile / Role Badge */}
                                            <Box sx={{ mb: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
                                                {user.PRF_LIB ? (
                                                    <Chip
                                                        icon={<BadgeIcon sx={{ '&&': { fontSize: 13 } }} />}
                                                        label={user.PRF_LIB}
                                                        size="small"
                                                        color="secondary"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        label="Sans Profil"
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.75rem', color: 'text.disabled' }}
                                                    />
                                                )}
                                                {user.USERDATA_LIB && (
                                                    <Chip
                                                        icon={<CardMembershipIcon sx={{ '&&': { fontSize: 13 } }} />}
                                                        label={user.USERDATA_LIB}
                                                        size="small"
                                                        variant="outlined"
                                                        sx={{ fontSize: '0.75rem' }}
                                                    />
                                                )}
                                            </Box>

                                            {/* Contact Details */}
                                            <Stack spacing={0.8} sx={{ mb: 2 }}>
                                                {user.USR_MAIL && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <EmailIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                            {user.USR_MAIL}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {user.USR_TELPOR && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PhoneIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                                        <Typography variant="body2" color="text.secondary" noWrap>
                                                            {user.USR_TELPOR}
                                                        </Typography>
                                                    </Box>
                                                )}
                                                {user.USR_START_DATE && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <CalendarIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            Depuis: {user.USR_START_DATE.split('T')[0]}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Stack>
                                        </CardContent>

                                        {/* Card Actions */}
                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pb: 2, gap: 1 }}>
                                            <IconButton size="small" color="primary" onClick={() => openEdit(user)}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton size="small" color="error" onClick={() => handleDelete(user)}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Card>
                                </Grid>
                            );
                        })}
                    </Grid>
                ) : (
                    /* ── TABLE VIEW ── */
                    <GenericTable
                        columns={[
                            { id: 'USR_CODE', label: t('page.users.field.code') || 'Code', minWidth: 110 },
                            { id: 'USR_LIB', label: t('page.users.field.lib') || 'Nom Utilisateur', minWidth: 180 },
                            {
                                id: 'PRF_LIB',
                                label: t('page.users.field.profile') || 'Profil',
                                minWidth: 140,
                                format: (val: any) => val ? (
                                    <Chip label={val} size="small" color="secondary" variant="outlined" sx={{ fontWeight: 600 }} />
                                ) : '-'
                            },
                            { id: 'USR_MAIL', label: t('page.users.field.email') || 'Email', minWidth: 180 },
                            { id: 'USR_TELPOR', label: t('page.users.field.mobile') || 'Téléphone', minWidth: 130 },
                            {
                                id: 'USR_START_DATE',
                                label: t('page.users.field.start_date') || 'Date Début',
                                minWidth: 120,
                                format: (val: any) => val ? String(val).split('T')[0] : '-'
                            },
                            {
                                id: 'USR_END_DATE',
                                label: t('page.users.field.end_date') || 'Date Fin',
                                minWidth: 120,
                                format: (val: any) => val ? String(val).split('T')[0] : '-'
                            },
                            { id: 'USERDATA_LIB', label: t('page.users.field.license') || 'Licence', minWidth: 140 }
                        ]}
                        data={usersList}
                        onEdit={(row) => openEdit(row as UserFormData)}
                        onDelete={(row) => handleDelete(row as UserFormData)}
                        filterColumns={['USR_CODE', 'USR_LIB', 'PRF_LIB']}
                        searchPlaceholder={t('page.users.search_placeholder') || "Rechercher un utilisateur..."}
                        maxHeight="calc(100vh - 340px)"
                    />
                )}
            </GenericPage>
        );
    }

    // ─── FORM VIEW (New / Edit) ────────────────────────────────────────────────

    const isEditing = Boolean(formData.USR_IDF);

    const footerInfo = {
        lastUpdate: formData.LAST_UPDT ? new Date(formData.LAST_UPDT).toLocaleString() : '-',
        userLib: formData.USER_LIB || '-'
    };

    return (
        <GenericPage<UserFormData>
            title={
                isEditing
                    ? (t('page.users.edit_title', formData.USR_LIB || formData.USR_CODE) || `Modifier l'utilisateur : ${formData.USR_LIB || formData.USR_CODE}`)
                    : (t('page.users.new_title') || "Nouvel Utilisateur")
            }
            icon={<PersonIcon />}
            footerEnabled={true}
            footerInfo={footerInfo}
            additionalActions={
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={backToList}
                >
                    {t('page.users.back_to_list') || "Retour à la liste"}
                </Button>
            }
            crudConfig={{
                entityKey: 'Blobtic_Sys_User,sys_user',
                primaryKey: 'USR_IDF'
            }}
            data={formData}
            onDataChange={(data) => {
                setFormData(data);
                loadUsers();
            }}
            onValidate={validate}
            onClear={handleClear}
        >
            <Box>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Grid container spacing={2.5}>
                    {/* Main User Fields */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('page.users.field.code') || "Code Utilisateur"}
                            value={formData.USR_CODE}
                            onChange={(e) => handleInputChange('USR_CODE', e.target.value)}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('page.users.field.lib') || "Nom Utilisateur"}
                            value={formData.USR_LIB}
                            onChange={(e) => handleInputChange('USR_LIB', e.target.value)}
                            required
                        />
                    </Grid>

                    {/* Profile Zoom (Secondary) */}
                    <Grid item xs={12} md={6}>
                        <ZoomField
                            zoomKey="profil"
                            label={t('page.users.field.profile') || "Profil"}
                            value={formData.PRF_CODE || ''}
                            displayField="PRF_CODE"
                            valueField="PRF_IDF"
                            onChange={(val, data) => handleZoomChange(val, data, 'profil')}
                            fullWidth
                        />
                    </Grid>

                    {/* License Zoom (Secondary) */}
                    <Grid item xs={12} md={6}>
                        <ZoomField
                            zoomKey="license"
                            label={t('page.users.field.license') || "Licence"}
                            value={formData.USERDATA_CODE || ''}
                            displayField="USERDATA_CODE"
                            valueField="USERDATA_IDF"
                            onChange={(val, data) => handleZoomChange(val, data, 'license')}
                            fullWidth
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('page.users.field.email') || "Email"}
                            value={formData.USR_MAIL || ''}
                            onChange={(e) => handleInputChange('USR_MAIL', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('page.users.field.mobile') || "Téléphone Portable"}
                            value={formData.USR_TELPOR || ''}
                            onChange={(e) => handleInputChange('USR_TELPOR', e.target.value)}
                            autoComplete="off"
                        />
                    </Grid>

                    {/* Dates */}
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('page.users.field.start_date') || "Date Début"}
                            type="date"
                            value={formData.USR_START_DATE ? formData.USR_START_DATE.split('T')[0] : ''}
                            onChange={(e) => handleInputChange('USR_START_DATE', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('page.users.field.end_date') || "Date Fin"}
                            type="date"
                            value={formData.USR_END_DATE ? formData.USR_END_DATE.split('T')[0] : ''}
                            onChange={(e) => handleInputChange('USR_END_DATE', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    {/* Password Section */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={t('page.users.field.password') || "Mot de passe"}
                            type={showPassword ? 'text' : 'password'}
                            value={formData.USR_PASS || ''}
                            onChange={(e) => handleInputChange('USR_PASS', e.target.value)}
                            autoComplete="new-password"
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={t('page.users.field.pass_date') || "Date Expiration Mot de Passe"}
                            type="date"
                            value={formData.USR_PASS_DATE ? formData.USR_PASS_DATE.split('T')[0] : ''}
                            onChange={(e) => handleInputChange('USR_PASS_DATE', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={t('page.users.field.pass_days') || "Jours avant Alerte expiration"}
                            type="number"
                            value={formData.USR_PASS_DAYS || ''}
                            onChange={(e) => handleInputChange('USR_PASS_DAYS', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('page.users.field.memo') || "Commentaire"}
                            multiline
                            rows={3}
                            value={formData.USR_MMO || ''}
                            onChange={(e) => handleInputChange('USR_MMO', e.target.value)}
                        />
                    </Grid>
                </Grid>
            </Box>
        </GenericPage>
    );
};

export default UsersPage;
